/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Commission ledger — GET /re/commissions (status, payee_type, broker_firm_id, project_id, booking_id),
 * POST /re/commissions/{id}/approve, POST /re/commissions/recalculate/{booking_id},
 * POST /re/commission-payouts {entry_ids} (APPROVED, one payee).
 */

import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Grid, Icon } from '@mui/material';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import { usePermission } from 'auth/usePermission';
import RePage, { FilterBar, LoadError, RePanel, useSnack } from './components/RePage';
import ReStatusChip from './components/ReStatusChip';
import ReTable from './components/ReTable';
import { RePicker, VocabSelect } from './components/Pickers';
import { KpiTile, Num } from './components/Widgets';
import {
  COMMISSION_PAYEE_TYPES, COMMISSION_STATUSES, formatMoney, formatPct, humanize, round2, toNumber, useReApi, useReList,
} from './api';
import { defaultCurrency } from '../variant';

const payeeKey = (c) => [c.payee_type, c.broker_firm_id || '', c.broker_agent_id || '', c.user_id || ''].join('|');

export default function CommissionsLedger() {
  const navigate = useNavigate();
  const { api } = useReApi();
  const [snack, notify] = useSnack();
  const { canEdit, canCreate } = usePermission('re_commission_entries');
  const [status, setStatus] = useState(null);
  const [payeeType, setPayeeType] = useState(null);
  const [firmId, setFirmId] = useState(null);
  const [projectId, setProjectId] = useState(null);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const list = useReList('/re/commissions', {
    status, payee_type: payeeType, broker_firm_id: firmId, project_id: projectId,
  }, { pageSize: 50 });

  const totals = useMemo(() => {
    const t = {};
    list.items.forEach((c) => { t[c.status] = round2((t[c.status] || 0) + (toNumber(c.total_amount) || 0)); });
    return t;
  }, [list.items]);

  const selectedRows = list.items.filter((c) => selected.includes(c.id));
  const payees = new Set(selectedRows.map(payeeKey));
  const selectedTotal = round2(selectedRows.reduce((a, c) => a + (toNumber(c.total_amount) || 0), 0));

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const approve = async (c) => {
    setBusy(true);
    try {
      await api.post(`/re/commissions/${c.id}/approve`);
      notify('Commission approved');
      list.reload();
    } catch (e) {
      notify.error(e, 'Could not approve');
    } finally {
      setBusy(false);
    }
  };

  const recalc = async (c) => {
    setBusy(true);
    try {
      await api.post(`/re/commissions/recalculate/${c.booking_id}`);
      notify(`Commissions for ${c.booking_number || `booking #${c.booking_id}`} recalculated`);
      list.reload();
    } catch (e) {
      notify.error(e, 'Could not recalculate');
    } finally {
      setBusy(false);
    }
  };

  const createPayout = async () => {
    setBusy(true);
    try {
      const res = await api.post('/re/commission-payouts', { entry_ids: selected });
      setSelected([]);
      notify(`Payout ${res.data?.payout_number || ''} created`);
      navigate(`/real-estate/commission-payouts?open=${res.data?.id || ''}`);
    } catch (e) {
      notify.error(e, 'Could not create the payout');
    } finally {
      setBusy(false);
    }
  };

  return (
    <RePage title="Commission ledger" subtitle="Broker and agent commission accrued on bookings and leases">
      <Grid container spacing={2} mb={2}>
        {['ACCRUED', 'ELIGIBLE', 'APPROVED', 'PAID', 'CLAWED_BACK'].map((s) => (
          <Grid item xs={6} md={2.4} key={s}>
            <KpiTile
              label={humanize(s)}
              tone={{ ACCRUED: 'neutral', ELIGIBLE: 'info', APPROVED: 'warning', PAID: 'success', CLAWED_BACK: 'error' }[s]}
              value={formatMoney(totals[s] || 0, defaultCurrency(), { compact: true })}
              hint="on this page"
              onClick={() => setStatus(s)}
            />
          </Grid>
        ))}
      </Grid>

      <FilterBar>
        <VocabSelect label="Status" value={status} onChange={(v) => { setStatus(v); setSelected([]); }} values={COMMISSION_STATUSES} />
        <VocabSelect label="Payee type" value={payeeType} onChange={setPayeeType} values={COMMISSION_PAYEE_TYPES} />
        <RePicker path="/re/broker-firms" label="Broker firm" value={firmId} onChange={setFirmId} />
        <RePicker path="/re/projects" label="Project" value={projectId} onChange={setProjectId} />
      </FilterBar>

      {selected.length > 0 && (
        <Alert
          severity={payees.size > 1 ? 'warning' : 'info'}
          sx={{ mb: 2 }}
          action={canCreate && (
            <MDButton size="small" variant="gradient" color="info" onClick={createPayout} disabled={busy || payees.size > 1}>
              <Icon>payments</Icon>&nbsp;Create payout
            </MDButton>
          )}
        >
          {payees.size > 1
            ? 'A payout can only contain entries for one payee. Select entries for a single payee.'
            : `${selected.length} approved entr${selected.length === 1 ? 'y' : 'ies'} selected · ${formatMoney(selectedTotal)}`}
        </Alert>
      )}

      <LoadError error={list.error} onRetry={list.reload} />
      <RePanel p={0}>
        <ReTable
          selectable={canCreate}
          selected={selected}
          onToggle={toggle}
          onToggleAll={setSelected}
          isRowSelectable={(c) => c.status === 'APPROVED' && !c.payout_id}
          loading={list.loading}
          rows={list.items}
          emptyMessage="No commission entries match these filters."
          total={list.total}
          page={list.page}
          pageSize={list.pageSize}
          onPageChange={(p) => { setSelected([]); list.setPage(p); }}
          onPageSizeChange={list.setPageSize}
          columns={[
            {
              key: 'ref', label: 'Deal',
              render: (c) => (c.booking_id ? (
                <MDButton size="small" variant="text" color="info" sx={{ p: 0, minWidth: 0 }} onClick={() => navigate(`/real-estate/bookings/${c.booking_id}`)}>
                  {c.booking_number || `Booking #${c.booking_id}`}
                </MDButton>
              ) : c.lease_id ? (
                <MDButton size="small" variant="text" color="info" sx={{ p: 0, minWidth: 0 }} onClick={() => navigate(`/real-estate/leases/${c.lease_id}`)}>
                  {c.lease_number || `Lease #${c.lease_id}`}
                </MDButton>
              ) : '—'),
            },
            { key: 'project_name', label: 'Project', render: (c) => [c.project_name, c.unit_number].filter(Boolean).join(' · ') || '—' },
            { key: 'payee_name', label: 'Payee', render: (c) => c.payee_name || humanize(c.payee_type) },
            { key: 'payee_type', label: 'Type', render: (c) => humanize(c.payee_type) },
            { key: 'basis_amount', label: 'Basis', align: 'right', render: (c) => <Num>{formatMoney(c.basis_amount, c.currency, { decimals: 0 })}</Num> },
            { key: 'rate_pct', label: 'Rate', align: 'right', render: (c) => (c.rate_pct != null ? formatPct(c.rate_pct, 2) : '—') },
            { key: 'gross_amount', label: 'Gross', align: 'right', render: (c) => <Num>{formatMoney(c.gross_amount, c.currency)}</Num> },
            { key: 'vat_amount', label: 'VAT', align: 'right', render: (c) => <Num>{formatMoney(c.vat_amount, c.currency)}</Num> },
            { key: 'total_amount', label: 'Total', align: 'right', render: (c) => <Num bold>{formatMoney(c.total_amount, c.currency)}</Num> },
            { key: 'status', label: 'Status', render: (c) => <ReStatusChip vocab="commission" status={c.status} size="xs" /> },
            {
              key: 'actions', label: '', align: 'right',
              render: (c) => (canEdit ? (
                <MDBox display="flex" gap={0.5} justifyContent="flex-end">
                  {c.status === 'ELIGIBLE' && (
                    <MDButton size="small" variant="text" color="success" onClick={() => approve(c)} disabled={busy}>Approve</MDButton>
                  )}
                  {c.booking_id && ['ACCRUED', 'ELIGIBLE'].includes(c.status) && (
                    <MDButton size="small" variant="text" color="info" onClick={() => recalc(c)} disabled={busy}>Recalc</MDButton>
                  )}
                </MDBox>
              ) : null),
            },
          ]}
        />
      </RePanel>
      {snack}
    </RePage>
  );
}
